import React, { useState, useEffect } from "react";
import { Card, Box, AvatarGroup, Button } from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import DataTable from "examples/Tables/DataTable";
import RefreesModal from "./Modals/refreesModal";
import CircularProgress from "@mui/material/CircularProgress";
import { getReferrals } from "services/referralService";
import { useDebouncedSearch } from "../../../hooks/useDebouncedSearch";
import SearchBar from "../Components/SearchBar";
import AvatarWithLoader from "../Components/Avatar/AvatarWithLoader";

function Referrals() {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [openViewReferrals, setOpenViewReferrals] = useState(false);
  const { searchTerm, debouncedSearchTerm, handleSearchChange } =
    useDebouncedSearch(500);

  const handleViewAll = (referral) => {
    setSelectedReferral(referral);
    setOpenViewReferrals(true);
  };

  const handleCloseModal = () => {
    setOpenViewReferrals(false);
    setSelectedReferral(null);
  };

  useEffect(() => {
    const fetchReferrals = async () => {
      try {
        setLoading(true);
        const data = await getReferrals(debouncedSearchTerm || null);
        setReferrals(data.data);
      } catch (error) {
        console.error("Error fetching referrals:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReferrals();
  }, [debouncedSearchTerm]);

  const columns = [
    {
      Header: "Business User",
      accessor: "businessUser",
      Cell: ({ row }) => {
        const user = row.original.businessUser;
        return (
          <ArgonBox display="flex" alignItems="center">
            <AvatarWithLoader src={user.profileImage} alt={user.name} />
            <Box>
              <ArgonTypography fontWeight="medium" variant="h6">
                {user.name}
              </ArgonTypography>
              <ArgonTypography variant="caption" color="text">
                {user.email}
              </ArgonTypography>
            </Box>
          </ArgonBox>
        );
      },
    },
    {
      Header: "Referral Code",
      accessor: "referralCode",
      Cell: ({ row }) => (
        <ArgonTypography fontWeight="medium" variant="h6">
          {row.original.referralCode}
        </ArgonTypography>
      ),
    },
    {
      Header: "Referred Users",
      accessor: "referredUsers",
      Cell: ({ row }) => {
        const users = row.original.referredUsers;
        const maxVisible = 4;

        return users.length > 0 ? (
          <Box display="flex" alignItems="center">
            <AvatarGroup
              max={maxVisible}
              total={users.length}
              sx={{
                "& .MuiAvatarGroup-avatar": {
                  backgroundColor: "#f2f2f2",
                  color: "#000",
                  fontSize: "1rem",
                },
              }}
            >
              {users.slice(0, maxVisible).map((user, index) => (
                <AvatarWithLoader
                  key={index}
                  src={user.profileImage}
                  alt={user.name}
                />
              ))}
            </AvatarGroup>

            <Button
              onClick={() => handleViewAll(row.original)}
              sx={{ color: "#5ad1c4", fontWeight: 600, fontSize: 14 }}
              size="small"
            >
              View All
            </Button>
          </Box>
        ) : (
          <ArgonTypography variant="caption" color="text">
            No referred users
          </ArgonTypography>
        );
      },
    },
  ];

  return (
    <>
      <SearchBar
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder="Search referrals"
      />
      <ArgonBox>
        <Card sx={{ overflow: "visible", mb: 10, mt: 5, padding: 3 }}>
          <ArgonBox p={3}>
            <ArgonTypography variant="h5" fontWeight="medium">
              Referrals
            </ArgonTypography>
          </ArgonBox>
          <ArgonBox>
            {loading ? (
              <ArgonBox display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </ArgonBox>
            ) : referrals.length > 0 ? (
              <DataTable
                table={{ columns, rows: referrals }}
                entriesPerPage={false}
                canSearch={false}
                showTotalEntries={false}
                pagination={false}
              />
            ) : (
              <ArgonBox display="flex" justifyContent="center" p={3}>
                <ArgonTypography variant="h6">
                  No referrals found
                </ArgonTypography>
              </ArgonBox>
            )}
          </ArgonBox>
        </Card>
      </ArgonBox>
      {openViewReferrals && (
        <RefreesModal
          open={openViewReferrals}
          onClose={handleCloseModal}
          selectedReferral={selectedReferral}
        />
      )}
    </>
  );
}

export default Referrals;
